import { answerTimeZone, canonicalReportingScopeMatches, canonicalSnapshotChannel, snapshotScopeMatches, windowEndMatches } from "./reportedSnapshotEvidence";

const METRIC_KEYS = ["spend", "clientFacingSpend", "billedSpend", "impressions", "clicks", "engagements", "video_views", "conversions"];
const EXACT_COUNT_KEYS = new Set(["impressions", "clicks", "engagements", "video_views"]);
const BUCKET_PUBLISHERS = { facebook: ["facebook", "audience_network", "messenger"], instagram: ["instagram"] };
const isRecord = value => value !== null && typeof value === "object" && !Array.isArray(value);
const hasFailure = value => value?.error != null || value?.errorCode != null || value?.ok === false;
const hasLatestWindowRefusal = body => [body.source, body.sourceTier, body.sourceLabel,
  ...(Array.isArray(body.sourceNotes) ? body.sourceNotes : []),
  ...(Array.isArray(body.sourceLimitations) ? body.sourceLimitations : [])]
  .filter(value => typeof value === "string").some(value => /latest stored|not the exact requested/i.test(value));
const readNumber = value => (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) &&
  Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : null;
const sameIdentity = (actual, expected) => ["number", "string"].includes(typeof actual) &&
  String(actual).trim() !== "" && String(actual) === String(expected);
const sameNumber = (a, b) => a === b || (a !== null && b !== null && Math.abs(a - b) <= Number.EPSILON * 16 * Math.max(1, Math.abs(a), Math.abs(b)));
const sameMetricNumber = (key, a, b) => EXACT_COUNT_KEYS.has(key) ? a === b : sameNumber(a, b);
const readMetrics = value => {
  if (!isRecord(value) || hasFailure(value)) return null;
  const metrics = {};
  for (const key of METRIC_KEYS) {
    if (value[key] == null) continue;
    if (EXACT_COUNT_KEYS.has(key) && typeof value[key] === "string" &&
        !/^\d+(?:\.0+)?$/.test(value[key].trim())) return null;
    const numeric = readNumber(value[key]);
    if (numeric === null || (EXACT_COUNT_KEYS.has(key) && !Number.isSafeInteger(numeric))) return null;
    metrics[key] = numeric;
  }
  return Object.keys(metrics).length ? metrics : null;
};
const sameMetrics = (a, b) => METRIC_KEYS.every(key => sameMetricNumber(key, a[key] ?? null, b[key] ?? null));

// Primary and recovery totals cover the whole requested publisher scope and
// must establish every applicable contribution. Canonical
// Facebook already includes Audience Network and Messenger: use its published
// raw-publisher decomposition once, never add their separate responses again.
export function resolveCompleteMetaRecovery(channelKeys, settledResults, scope) {
  const responses = new Map();
  for (const [index, channel] of channelKeys.entries()) {
    const result = settledResults[index];
    const body = result?.status === "fulfilled" ? result.value : null;
    if (!isRecord(body) || typeof body.found !== "boolean" ||
        hasFailure(body) || hasLatestWindowRefusal(body) ||
        !sameIdentity(body.advertiserId, scope.advertiserId) ||
        canonicalSnapshotChannel(body.channel) !== canonicalSnapshotChannel(channel) ||
        (scope.campaignId != null ? !sameIdentity(body.campaignId, scope.campaignId) : body.campaignId != null) ||
        // The end is the requested end, or the end for the calendar the answer
        // states (any accepted calendar's when it states none).
        !isRecord(body.window) || body.window.start !== scope.timeframe.start || body.window.end == null ||
        !windowEndMatches(body.window.end, scope.timeframe, answerTimeZone(body)) ||
        !snapshotScopeMatches(body, { ...scope, channel })) return null;
    if (body.found && !readMetrics(body.kpis)) return null;
    if (!body.found && (body.snapshot !== null || body.sourceTier !== null ||
        body.canonicalSummary != null || body.canonicalReporting != null ||
        !isRecord(body.kpis) || !Object.keys(body.kpis).length || Object.values(body.kpis).some(value => value !== null) ||
        body.dataFreshness?.basis !== "UNAVAILABLE" || body.dataFreshness?.complete !== false)) return null;
    responses.set(channel, body);
  }

  const contributions = new Map();
  for (const [bucket, publishers] of Object.entries(BUCKET_PUBLISHERS)) {
    const body = responses.get(bucket);
    if (body?.found !== true || body.snapshot?.sourceContract?.contract !== "canonical_reporting_v1") continue;
    // usedFallback alone describes valid stored contributors, not a different
    // window. Exact canonical scope plus the raw publisher decomposition is
    // the applicable evidence; explicit latest-window refusals remain above.
    if (body.kpis?.hasReportedDeliveryData !== true || !isRecord(body.kpis.metaSubChannels)) return null;
    const summaries = [body.canonicalSummary, body.canonicalReporting].filter(value => value != null);
    if (!summaries.length || summaries.some(summary => hasFailure(summary) || summary.contract !== "canonical_reporting_v1" ||
        !canonicalReportingScopeMatches(summary, scope))) return null;
    const subChannels = body.kpis.metaSubChannels;
    const keys = Object.keys(subChannels);
    if (!keys.length || keys.some(channel => !publishers.includes(channel))) return null;
    const metricsByPublisher = new Map();
    for (const channel of keys) {
      const metrics = readMetrics(subChannels[channel]);
      if (!metrics) return null;
      metricsByPublisher.set(channel, metrics);
    }
    const bucketMetrics = readMetrics(body.kpis);
    for (const key of METRIC_KEYS) {
      const values = [...metricsByPublisher.values()].map(metrics => metrics[key]).filter(value => value != null);
      const sum = values.length ? values.reduce((total, value) => total + value, 0) : null;
      if ((sum !== null && (!Number.isFinite(sum) || (EXACT_COUNT_KEYS.has(key) && !Number.isSafeInteger(sum)))) ||
          !sameMetricNumber(key, bucketMetrics[key] ?? null, sum)) return null;
    }
    for (const summary of summaries) {
      const canonical = summary.byChannel?.[bucket];
      const canonicalMetrics = readMetrics(canonical);
      if (!canonicalMetrics || !sameMetrics(canonicalMetrics, bucketMetrics) || !isRecord(canonical.metaSubChannels) ||
          Object.keys(canonical.metaSubChannels).length !== keys.length) return null;
      for (const channel of keys) {
        const metrics = readMetrics(canonical.metaSubChannels[channel]);
        if (!metrics || !sameMetrics(metrics, metricsByPublisher.get(channel))) return null;
      }
    }
    for (const [channel, metrics] of metricsByPublisher) {
      if (channelKeys.includes(channel)) contributions.set(channel, { metrics, body, evidence: "canonical_meta_subchannel" });
    }
  }

  for (const channel of channelKeys) {
    const body = responses.get(channel);
    const existing = contributions.get(channel);
    if (existing) {
      // A child endpoint may be unavailable even when the canonical bucket
      // explicitly reports that child. An error or conflicting response was
      // rejected above; found:false itself supplies no measurement or zero.
      if (body.found && body !== existing.body) {
        const direct = readMetrics(body.kpis);
        if (!direct) return null;
        const storedScope = body.snapshot?.sourceScope;
        const storedWindow = storedScope?.window;
        const fullWindow = scope.timeframe;
        const narrowerStoredWindow = storedScope?.contract === "stored_snapshot_scope_v1" &&
          storedWindow?.start >= fullWindow.start && storedWindow?.end <= fullWindow.end &&
          (storedWindow.start !== fullWindow.start || storedWindow.end !== fullWindow.end);
        if (narrowerStoredWindow) {
          // The direct endpoint can return one recorded day while the canonical
          // bucket covers several. Their values have different periods: do not
          // demand equality or add this contributor a second time. Require the
          // actual stored record in canonical lineage, and retain additive
          // bounds. The verified canonical decomposition remains the total.
          if (typeof body.snapshot?.date_start !== "string" || typeof body.snapshot?.date_end !== "string" ||
              body.snapshot.date_start.slice(0, 10) !== storedWindow.start ||
              body.snapshot.date_end.slice(0, 10) !== storedWindow.end ||
              canonicalSnapshotChannel(body.snapshot.channel) !== canonicalSnapshotChannel(channel)) return null;
          const contributors = existing.body.dataFreshness?.contributors;
          const pinnedContributor = Array.isArray(contributors) && contributors.some(contributor =>
            contributor?.complete === true && contributor.status === "SUCCESS" && !contributor.failureReason &&
            sameIdentity(contributor.sourceIdentity?.snapshotId, body.snapshot?.id) &&
            sameIdentity(contributor.sourceIdentity?.mediaCampaignId, storedScope.campaignId) &&
            typeof body.snapshot?.data_source === "string" &&
            contributor.source === body.snapshot.data_source
          );
          if (!pinnedContributor || METRIC_KEYS.some(key => direct[key] != null &&
              (existing.metrics[key] == null || direct[key] > existing.metrics[key]))) return null;
        } else if (METRIC_KEYS.some(key => direct[key] != null &&
            !sameMetricNumber(key, direct[key], existing.metrics[key] ?? null))) return null;
      }
      continue;
    }
    // No canonical decomposition means only a positively reported, independently
    // bound stored publisher can complete coverage. Absent rows never do so.
    const metrics = body.found === true ? readMetrics(body.kpis) : null;
    if (!metrics || body.snapshot?.sourceScope?.contract !== "stored_snapshot_scope_v1" ||
        body.snapshot.sourceScope.window?.start !== scope.timeframe.start ||
        body.snapshot.sourceScope.window?.end == null ||
        !windowEndMatches(body.snapshot.sourceScope.window.end, scope.timeframe, answerTimeZone(body)) ||
        !Object.values(metrics).some(value => value > 0)) return null;
    contributions.set(channel, { metrics, body, evidence: "stored_publisher_scope" });
  }

  const kpis = {};
  for (const key of METRIC_KEYS) {
    const values = channelKeys.map(channel => contributions.get(channel).metrics[key]);
    kpis[key] = values.every(value => value != null) ? values.reduce((total, value) => total + value, 0) : null;
    if (kpis[key] !== null && (!Number.isFinite(kpis[key]) ||
        (EXACT_COUNT_KEYS.has(key) && !Number.isSafeInteger(kpis[key])))) return null;
  }
  if (!Object.values(kpis).some(value => value !== null)) return null;
  const spend = kpis.billedSpend ?? kpis.clientFacingSpend ?? kpis.spend;
  kpis.ctr = kpis.clicks !== null && kpis.impressions > 0 ? kpis.clicks / kpis.impressions * 100 : null;
  kpis.cpm = spend !== null && kpis.impressions > 0 ? spend / kpis.impressions * 1000 : null;
  kpis.cpc = spend !== null && kpis.clicks > 0 ? spend / kpis.clicks : null;
  for (const key of ["ctr", "cpm", "cpc"]) if (!Number.isFinite(kpis[key])) kpis[key] = null;
  return {
    kpis,
    reportedZero: METRIC_KEYS.some(key => kpis[key] === 0),
    settledResults: channelKeys.map(channel => ({ status: "fulfilled", value: {
      ...contributions.get(channel).body, kpis: contributions.get(channel).metrics,
    } })),
    publishers: channelKeys.map(channel => ({ channel, evidence: contributions.get(channel).evidence })),
  };
}
